import React from 'react'
import { makeStyles } from '@material-ui/styles'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import DropdownField from 'core/components/validatedForm/DropdownField'
import Dropdown from 'core/elements/dropdown'
import Theme from 'core/themes/model'

const useStyles = makeStyles<Theme>((theme) => ({
  gpuConfig: {
    display: 'grid',
    gap: theme.spacing(2),
    alignItems: 'start',
    gridTemplateRows: 'max-content max-content',
  },
  gpuToggleSwitch: {
    display: 'grid',
    gridTemplateColumns: 'max-content auto',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  gpuModelDiv: {
    display: 'grid',
    gridTemplateColumns: '346px 346px',
    alignItems: 'flex-end',
    gap: theme.spacing(4),
  },
  srIovDeviceToggleSwitch: {
    marginBottom: theme.spacing(1.5),
  },
  switchTooltip: {
    padding: theme.spacing(1),
    color: theme.palette.common.white,
  },
}))

interface HostNetworkGpuConfigProps {
  params: any
  config: any
  idx: number
  enableGpuPassthrough: boolean
  setEnableGpuPassthrough: (value: boolean) => void
  existingHostNetworkConfig: any
  passthroughList: { label: string; value: string }[]
  updateField: (value: any, prop: string) => void
  isUsedHostConfig: boolean
}

export default function HostNetworkGpuConfig({
  params,
  config,
  idx,
  enableGpuPassthrough,
  setEnableGpuPassthrough,
  existingHostNetworkConfig,
  passthroughList,
  updateField,
  isUsedHostConfig,
}: HostNetworkGpuConfigProps) {
  const classes = useStyles()
  return (
    <div className={classes.gpuConfig}>
      <Tooltip
        message={
          isUsedHostConfig
            ? 'Cannot update GPU passthrough mode if host config is already in use'
            : ''
        }
      >
        <div className={classes.gpuToggleSwitch}>
          <ToggleSwitchField
            id={`hostNetworkConfigs.${idx}.enableGpuPassthrough`}
            label="Enable GPU Passthrough"
            value={enableGpuPassthrough}
            onChange={(value) => {
              if (!value) {
                updateField([], 'gpuPciPassthroughs')
              }
              setEnableGpuPassthrough(value)
            }}
            disabled={isUsedHostConfig && existingHostNetworkConfig?.gpuPciPassthroughs?.length > 0}
            customTooltipInfoBody={
              <Text variant="body2" className={classes.switchTooltip}>
                Enable GPU passthrough to attach GPU devices directly to VMs.
              </Text>
            }
          />
        </div>
      </Tooltip>
      {enableGpuPassthrough && (
        <div className={classes.gpuModelDiv}>
          <DropdownField
            DropdownComponent={Dropdown}
            id={`hostNetworkConfigs.${idx}.gpuPciPassthroughs`}
            label="GPU Model"
            info={'Select a GPU model supported by this configuration'}
            onChange={(value) => {
              updateField([value], 'gpuPciPassthroughs')
            }}
            value={config?.gpuPciPassthroughs?.[0]}
            data-testid="passthroughs-multi-select"
            items={passthroughList}
            disabled={isUsedHostConfig && existingHostNetworkConfig?.gpuPciPassthroughs?.length > 0}
            required
          />
          <ToggleSwitchField
            id={`hostNetworkConfigs.${idx}.srIOVDevice`}
            label="SR-IOV Device"
            onChange={(value) => {
              updateField(value, 'srIOVDevice')
            }}
            value={!!config?.srIOVDevice}
            disabled={isUsedHostConfig && existingHostNetworkConfig?.gpuPciPassthroughs?.length > 0}
            customTooltipInfoBody={
              <Text variant="body2" className={classes.switchTooltip}>
                Enable this if the GPU device supports SR-IOV.
              </Text>
            }
            className={classes.srIovDeviceToggleSwitch}
          />
        </div>
      )}
    </div>
  )
}
